// «Grupper» under Tjenester og priser: gruppene tjenestene vises i på nettsiden (f.eks. «El-sykkel»,
// «Alpint»). Navn, infotekst, rekkefølge og av/på. Hvilke tjenester som står i en gruppe, velges
// på tjenesten.
import React, { useState } from 'react';
import { Tekstfelt, Valgfelt, knappPrimar, knappSekundar } from '../../ny-kundesamhandling/Skjema';
import Ikon from '../../ny-kundesamhandling/Ikon';
import { KATEGORI_NAVN, lagreIKatalog, sistEndret, endredeFelt } from './katalogFelles';
import { Avkrysning, Tallfelt, Melding, Endringslogg } from './Velgere';

function Gruppeskjema({ gruppe, kanEndre, onLagret, onAvbryt }) {
  const ny = !gruppe;
  const [original, setOriginal] = useState(() => ({
    tjenestetype: gruppe?.tjenestetype || '',
    kategori: gruppe?.kategori || 'sykkelservice',
    tjenesteinfo: gruppe?.tjenesteinfo || '',
    sortering: gruppe?.sortering ?? null,
    active: gruppe ? gruppe.active !== false : true,
  }));
  const [skjema, setSkjema] = useState(original);
  const [status, setStatus] = useState({ type: '', tekst: '' });
  const sett = (felt) => (v) => setSkjema((s) => ({ ...s, [felt]: v }));
  const id = `g-${gruppe?._id || 'ny'}`;

  async function lagre(e) {
    e.preventDefault();
    if (!skjema.tjenestetype.trim()) { setStatus({ type: 'feil', tekst: 'Skriv navnet på gruppen.' }); return; }
    setStatus({ type: 'lagrer', tekst: '' });
    try {
      await lagreIKatalog(ny ? 'POST' : 'PATCH', ny ? 'grupper' : `grupper/${gruppe._id}`, ny ? skjema : endredeFelt(skjema, original));
      setOriginal(skjema);
      setStatus({ type: 'ok', tekst: 'Lagret' });
      onLagret();
    } catch (err) {
      setStatus({ type: 'feil', tekst: err.message });
    }
  }

  return (
    <form onSubmit={lagre} className="space-y-3">
      <fieldset disabled={!kanEndre} className="space-y-3">
        <legend className="sr-only">{ny ? 'Ny gruppe' : `Gruppen ${gruppe.tjenestetype}`}</legend>
        <div className="grid gap-3 sm:grid-cols-2">
          <Tekstfelt id={`${id}-navn`} etikett="Navn" value={skjema.tjenestetype} onChange={(e) => sett('tjenestetype')(e.target.value)} required />
          <Valgfelt id={`${id}-kat`} etikett="Kategori" value={skjema.kategori} onChange={(e) => sett('kategori')(e.target.value)}>
            {Object.entries(KATEGORI_NAVN).map(([v, n]) => <option key={v} value={v}>{n}</option>)}
          </Valgfelt>
        </div>
        <Tekstfelt id={`${id}-info`} etikett="Infotekst på nettsiden" flerlinjet value={skjema.tjenesteinfo} onChange={(e) => sett('tjenesteinfo')(e.target.value)} />
        <div className="flex flex-wrap items-end gap-3">
          <Tallfelt id={`${id}-sort`} etikett="Rekkefølge" hjelp="Lavest først" verdi={skjema.sortering} onEndre={sett('sortering')} />
          <Avkrysning id={`${id}-aktiv`} etikett="Vises på nettsiden" verdi={skjema.active} onEndre={sett('active')} />
        </div>
      </fieldset>
      {status.type === 'feil' && <Melding type="feil">{status.tekst}</Melding>}
      {status.type === 'ok' && <Melding type="ok">{status.tekst}</Melding>}
      {kanEndre && (
        <div className="flex flex-wrap gap-2">
          <button type="submit" disabled={status.type === 'lagrer'} className={knappPrimar}>
            <Ikon navn="hake" className="w-6 h-6" /> {status.type === 'lagrer' ? 'Lagrer …' : ny ? 'Opprett gruppe' : 'Lagre'}
          </button>
          {onAvbryt && <button type="button" onClick={onAvbryt} className={knappSekundar}>Avbryt</button>}
        </div>
      )}
    </form>
  );
}

export default function Grupper({ data, kanEndre, hent }) {
  const [visNy, setVisNy] = useState(false);
  const antall = (id) => data.tjenester.filter((t) => (t.grupper || []).includes(id) && t.aktiv !== false).length;

  return (
    <div className="space-y-3">
      <p className="text-slate-700">Gruppene tjenestene vises i på nettsiden. Hvilke tjenester som står i en gruppe, velger du på tjenesten.</p>
      {kanEndre && !visNy && (
        <button type="button" onClick={() => setVisNy(true)} className={knappSekundar}><Ikon navn="pluss" className="w-5 h-5" /> Ny gruppe</button>
      )}
      {visNy && (
        <section aria-label="Ny gruppe" className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-3">
          <h2 className="mb-3 text-base font-bold text-slate-900">Ny gruppe</h2>
          <Gruppeskjema kanEndre={kanEndre} onLagret={() => { setVisNy(false); hent(); }} onAvbryt={() => setVisNy(false)} />
        </section>
      )}
      <ul className="space-y-2">
        {data.grupper.map((g) => (
          <li key={g._id}>
            <details className={`rounded-2xl border-2 bg-white p-3 ${g.active === false ? 'border-dashed border-slate-300' : 'border-slate-200'}`}>
              <summary className="flex cursor-pointer flex-wrap items-center gap-2">
                <span className="font-bold text-slate-900">{g.tjenestetype}</span>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-sm text-slate-800">{KATEGORI_NAVN[g.kategori]}</span>
                {g.active === false && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-sm font-semibold text-amber-950">Av</span>}
                <span className="text-sm text-slate-700">{antall(g._id)} tjenester</span>
              </summary>
              <div className="mt-4 space-y-2">
                <Gruppeskjema gruppe={g} kanEndre={kanEndre} onLagret={hent} />
                <p className="text-sm text-slate-600">{sistEndret(g)}</p>
                <Endringslogg endringer={g.endringer} />
              </div>
            </details>
          </li>
        ))}
      </ul>
    </div>
  );
}
